import * as React from 'react';
import { useSnackbar } from 'notistack';
import useAuth from 'hooks/useAuth';
import axios from '../../utils/axios';
import { isMaster, podeGravarCadastroAdmin } from '../../utils/perfil';
import { HistoricoGPS } from '../historicoGPS';
import { Num, SeletorStatus, statusTexto } from './index';
import ModalPassos, { PassoModal } from './ModalPassos';
import { AreaArquivo, Campo, classesEntrada } from './Modal';
import { cn } from '../../lib/utils';

/* ============================================================================
   Cadastro de APK em modal de cartoes.

   Substitui /dashboard/apks/new/:id para criar e editar. A pagina antiga
   continua no ar.

   Conferido contra apks/new/[id].tsx. Tres cartoes: Arquivo · Dados · Historico.

   QUATRO REGRAS DO FORMULARIO ANTIGO MANTIDAS:

   1. PACOTE E VERSAO SAO LIDOS DO ARQUIVO, nao digitados. O backend abre o APK e
      extrai `package`, `versionName` e `versionCode` do manifesto
      (apks.service.ts:100-105). Na tela antiga os dois campos existem com
      `disabled` - aqui aparecem como leitura, sem parecer campo editavel.

   2. APK APAGADA NAO PODE SER ALTERADA (`oldEntity.deletedAt`).

   3. `FormData` com `multipart/form-data`, mesmos nomes de campo.

   4. O BOTAO OCULTO DO MASTER: "Salvar e forcar APK nova". O backend recusa
      pacote+versionCode ja cadastrados; esta acao ignora a trava. Serve ao caso
      em que a fabricante republica um binario corrigido SEM subir o versionCode.
      Continua sendo `forcar: 'true'` no corpo, e SO vai quando pedido - mandar
      'false' faria o backend registrar tentativa de forcar em toda gravacao
      normal. Fica como acao secundaria no cabecalho, nao ao lado do Salvar.

   ---------------------------------------------------------------------------
   🔴 DEFEITO CORRIGIDO: A TRILHA DE AUDITORIA NUNCA ERA ENVIADA.

   O formulario antigo monta `listCampoData` (nome, pacote, status) e depois faz:

       if (data.dataCampo) formData.append('dataCampo', ...)

   `dataCampo` NAO EXISTE nos valores do formulario - nao esta nos
   `defaultValues` e nenhum `setValue` o define. A condicao e sempre falsa, o
   `append` nunca acontece e a lista calculada e descartada. Ou seja: **nenhuma
   alteracao de APK jamais foi registrada no historico.**

   Aqui o `dataCampo` e anexado quando ha edicao, sem guarda impossivel.
   ============================================================================ */

type Apk = {
  codigo?: number;
  nome?: string;
  packageName?: string;
  versionName?: string;
  versionCode?: string;
  status?: number | string;
  url?: string;
  fabricante?: string;
  deletedAt?: string | null;
};

export default function ModalApk({
  aberto,
  codigo,
  onFechar,
  onSalvo,
}: {
  aberto: boolean;
  /** `undefined` ou '0' = criar. Qualquer outro valor = editar. */
  codigo?: string | number;
  onFechar: () => void;
  onSalvo?: () => void;
}) {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const editando = Boolean(codigo && Number(codigo) > 0);

  const [nome, setNome] = React.useState('');
  const [status, setStatus] = React.useState('ATIVO');
  const [arquivo, setArquivo] = React.useState<File | null>(null);
  const [original, setOriginal] = React.useState<Apk>({});
  const [salvando, setSalvando] = React.useState(false);
  const [carregando, setCarregando] = React.useState(false);

  const apagada = Boolean(original?.deletedAt);
  const podeGravar = podeGravarCadastroAdmin(user);

  React.useEffect(() => {
    if (!aberto) return;

    setNome('');
    setStatus('ATIVO');
    setArquivo(null);
    setOriginal({});

    if (!editando) return;

    setCarregando(true);
    axios
      .get<Apk>(`/apks/byId/${codigo}`)
      .then((r) => {
        const d = r.data || {};
        setOriginal(d);
        setNome(d.nome ?? '');
        setStatus(statusTexto(d.status));
      })
      .catch((e: any) =>
        enqueueSnackbar(e?.response?.data?.message || 'Não foi possível carregar a APK.', { variant: 'error' })
      )
      .finally(() => setCarregando(false));
  }, [aberto, codigo, editando, enqueueSnackbar]);

  const nomeOk = nome.trim().length > 0 && nome.trim().length <= 150;
  // Na CRIACAO o arquivo e obrigatorio - e dele que saem pacote e versao.
  const arquivoOk = editando ? true : Boolean(arquivo);

  async function salvar(forcar = false) {
    if (salvando || !podeGravar) return;
    if (apagada) {
      enqueueSnackbar('Aplicativo foi deletado, não pode alterar mais', { variant: 'warning' });
      return;
    }
    setSalvando(true);
    try {
      const diff: Array<{ campo: string; antigo: any; novo: any }> = [];
      if (editando) {
        if (nome !== original.nome) diff.push({ campo: 'Nome', antigo: original.nome, novo: nome });
        if (status !== String(original.status))
          diff.push({ campo: 'Status', antigo: original.status, novo: status });
      }

      const formData = new FormData();
      if (editando) formData.append('codigo', String(codigo));
      if (nome) formData.append('nome', nome.trim());
      if (status) formData.append('status', status);
      // Pacote nao e enviado: o backend le do manifesto do arquivo, e na edicao
      // sem arquivo novo ele mantem o que ja esta gravado.
      if (arquivo) formData.append('file', arquivo);

      // Anexado sem a guarda impossivel do formulario antigo - ver cabecalho.
      if (editando) formData.append('dataCampo', JSON.stringify(diff));

      // So vai no corpo quando pedido de fato.
      if (forcar) formData.append('forcar', 'true');

      await axios[editando ? 'put' : 'post'](`/apks/${editando ? codigo : ''}`, formData, {
        headers: { 'content-type': 'multipart/form-data' },
      });

      enqueueSnackbar('Salvo com sucesso', { variant: 'success' });
      onSalvo?.();
      onFechar();
    } catch (err: any) {
      const e = err?.response?.data;
      const msg = e?.errors?.length ? e.errors[0]?.message : e?.message;
      enqueueSnackbar(msg || 'Ops ocorreu um erro no sistema.', {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
      });
    } finally {
      setSalvando(false);
    }
  }

  const passos: PassoModal[] = [
    {
      id: 'arquivo',
      titulo: 'Arquivo',
      grupo: 'Cadastro',
      resumo: editando ? 'arquivo publicado' : arquivo ? arquivo.name : 'nenhum',
      valido: arquivoOk,
      pendencia: 'arquivo .apk',
      conteudo: (
        <>
          {apagada && (
            <div className="rounded-lg border border-danger bg-danger-soft p-3 text-[12px] text-danger">
              Esta APK foi <strong>apagada</strong> e não pode mais ser alterada.
            </div>
          )}

          <Campo bloco
            rotulo={editando ? 'Substituir arquivo' : 'Arquivo .apk'}
            obrigatorio={!editando}
            dica="O servidor lê pacote, versão e versionCode do manifesto do arquivo."
          >
            <AreaArquivo
              arquivo={arquivo}
              onArquivo={setArquivo}
              accept=".apk,application/vnd.android.package-archive"
              extensoes={['.apk']}
              disabled={apagada || !podeGravar}
              rotulo={editando ? 'Arraste a APK nova aqui ou clique para escolher' : 'Arraste a .apk aqui ou clique para escolher'}
              dica="Somente arquivos .apk"
            />
          </Campo>

          {/* Pacote e versao como LEITURA, nao campo desabilitado: eles nao sao
              digitaveis nunca - saem do manifesto. */}
          {editando && (
            <div className="rounded-lg border border-line bg-surface-2 p-3">
              <div className="flex items-start justify-between gap-3 py-1">
                <span className="text-[12px] font-semibold">Pacote</span>
                <Num className="text-right text-[11px] text-muted-foreground">
                  {original.packageName || '—'}
                </Num>
              </div>
              <div className="flex items-start justify-between gap-3 py-1">
                <span className="text-[12px] font-semibold">Versão</span>
                <span className="text-right text-[11px] text-muted-foreground">
                  {original.versionName || '—'} (<Num>{original.versionCode || '—'}</Num>)
                </span>
              </div>
              {original.url && (
                <div className="flex items-start justify-between gap-3 py-1">
                  <span className="text-[12px] font-semibold">Download</span>
                  <a
                    download
                    href={`${process.env.HOST_API_APK}/${original.url}`}
                    className="text-right text-[11px] font-semibold text-primary hover:underline"
                  >
                    baixar arquivo atual
                  </a>
                </div>
              )}
            </div>
          )}

          <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px] text-muted-foreground">
            O servidor recusa <Num>pacote + versionCode</Num> já cadastrados. Se a fabricante republicou o
            binário sem subir o versionCode, use a ação <strong>forçar</strong> no topo — disponível só para
            Master.
          </div>
        </>
      ),
    },

    {
      id: 'dados',
      titulo: 'Dados',
      grupo: 'Cadastro',
      resumo: nome.trim() || 'sem nome',
      valido: nomeOk && Boolean(status),
      pendencia: !nomeOk ? 'nome' : 'status',
      conteudo: (
        <>
          <Campo rotulo="Nome" obrigatorio>
            <input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              maxLength={150}
              disabled={apagada || !podeGravar}
              className={classesEntrada}
            />
          </Campo>

          <Campo bloco rotulo="Status" obrigatorio>
            <SeletorStatus valor={status} onChange={setStatus} />
            {status === 'INATIVO' && (
              <span className="mt-1.5 block text-[11px] text-muted-foreground">
                Inativa sai da lista de escolha dos grupos, mas o arquivo continua servido no link atual.
              </span>
            )}
          </Campo>

          {original.fabricante && (
            <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px] text-muted-foreground">
              Fabricante: <strong className="text-ink">{original.fabricante}</strong>
            </div>
          )}

          {!podeGravar && (
            <div className="rounded-lg border border-line bg-warn-soft p-3 text-[12px] text-warn">
              Seu perfil não pode gravar cadastro de APK.
            </div>
          )}
        </>
      ),
    },
  ];

  passos.push({
    id: 'historico',
    titulo: 'Histórico',
    grupo: 'Ferramentas',
    informativo: true,
    conteudo: original.codigo ? (
      <HistoricoGPS tipo={'APK'} chave={original.codigo} />
    ) : (
      <div className="rounded-lg border border-line bg-surface-2 p-4 text-[12px] text-muted-foreground">
          O histórico começa a ser gravado quando este cadastro for salvo. Cada alteração
          posterior fica registrada aqui com quem fez, quando e o que mudou.
        </div>
    ),
  });

  const tudoValido = nomeOk && arquivoOk && Boolean(status) && !apagada && podeGravar;

  return (
    <ModalPassos
      aberto={aberto}
      titulo={editando ? 'Editar APK' : 'Nova APK'}
      subtitulo={
        carregando ? 'Carregando...' : editando ? `${original.nome ?? ''} · ${original.packageName ?? ''}` : 'Nova APK'
      }
      passos={passos}
      rotuloFinalizar={editando ? 'Salvar' : 'Cadastrar APK'}
      onFinalizar={() => salvar(false)}
      onFechar={onFechar}
      salvando={salvando}
      acaoExtra={
        // Somente MASTER ve. `outlined`/ambar de proposito: e acao de excecao e
        // nao deve competir visualmente com o Salvar.
        isMaster(user) && arquivo ? (
          <button
            type="button"
            onClick={() => salvar(true)}
            disabled={!tudoValido || salvando}
            title="Ignora a trava de pacote + versão já cadastrada e grava um arquivo (e link) novos. Somente Master."
            className="h-9 rounded-lg border border-warn bg-warn-soft px-3 text-[12px] font-bold text-warn disabled:opacity-40"
          >
            Salvar e forçar APK nova
          </button>
        ) : null
      }
    />
  );
}
